import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import PremiumPageHeader from "../../components/PremiumPageHeader";
import { ListPageFloatingControls } from "../../components/ListPageLayout";
import ListViewSettings from "../../components/ListViewSettings";
import "../../button.css";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { buildListExcelExportHeader } from "../../utils/listExcelExportHeader";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from '@mui/material';
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { displayError } from "../../helpers";
import moment from 'moment';
import { getAllDeliveryChallans, deleteDeliveryChallan, printBatchReport, printDeliveryChallan } from "../../services/DeliveryChallanServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { buildDeliveryChallanQueryParams } from "../../utils/listViewDateRanges";
import TodayDcDetailModal from "./TodayDcDetailModal";
import { mapDeliveryChallanListRow } from "./todayDcDetailUtils";
import { getDeliveryChallanDocumentLabel } from "./deliveryChallanDocumentTypes";

function DeliveryChallanTable({ viewOnly: viewOnlyProp = false }) {
    const InitialData = {
        total_pages: 0,
        records: [],
      };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate(); 
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);  
    const left_pinned = ["delivery_challan_no","delivery_challan_date"]
    const right_pinned = ["id"]
    const [listViewConfig, setListViewConfig] = useState(null);
    const [todayDcModalOpen, setTodayDcModalOpen] = useState(false);

    const sumQty = (records, field) =>
        records.reduce((sum, row) => sum + (Number(row[field]) || 0), 0);

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const qtyTotals = useMemo(() => {
        const todayRecords = data.records.filter(
            (row) => row.delivery_challan_date === todayDateLabel
        );
        return {
            production_qty: sumQty(data.records, 'production_qty'),
            dc_qty: sumQty(data.records, 'dc_qty'),
            accepted_qty: sumQty(data.records, 'accepted_qty'),
            today_dc_qty: sumQty(todayRecords, 'dc_qty'),
        };
    }, [data.records, todayDateLabel]);

    const formatQty = (value) => Number(value || 0).toFixed(2);

    const todayDcCard = useMemo(() => ({
        label: 'Today DC',
        value: formatQty(qtyTotals.today_dc_qty),
        gradient: 'linear-gradient(135deg, #186098 0%, #0090D8 55%, #5EC8F0 100%)',
        shadow: '0 8px 20px rgba(0, 144, 216, 0.28)',
    }), [qtyTotals.today_dc_qty]);

    const summaryCards = useMemo(() => ([
        {
            label: 'Production',
            value: formatQty(qtyTotals.production_qty),
            gradient: 'linear-gradient(135deg, #186098 0%, #0090D8 55%, #40B8E8 100%)',
            shadow: '0 8px 20px rgba(0, 144, 216, 0.24)',
        },
        {
            label: 'DC Qty',
            value: formatQty(qtyTotals.dc_qty),
            gradient: 'linear-gradient(135deg, #305870 0%, #186098 55%, #5AA8D8 100%)',
            shadow: '0 8px 20px rgba(48, 88, 112, 0.24)',
        },
        {
            label: 'Accepted',
            value: formatQty(qtyTotals.accepted_qty),
            gradient: 'linear-gradient(135deg, #0E6A98 0%, #0090D8 55%, #40B8E8 100%)',
            shadow: '0 8px 20px rgba(0, 144, 216, 0.24)',
        },
    ]), [qtyTotals]);

    const loadDeliveryChallanList = useCallback((config) => {
        if (!config) return;
        setIsLoading(true);
        getAllDeliveryChallans(cookies, buildDeliveryChallanQueryParams(config))
        .then(deliverychallanList => {
            if (!deliverychallanList?.delivery_challan_list) {
                throw new Error('Delivery challan list was not returned by the server.');
            }
            const tableData = deliverychallanList.delivery_challan_list
            .map(mapDeliveryChallanListRow);

            setData({ records: tableData });
            setIsLoading(false);
        })
        .catch((error) => {
            console.log(error.response?.data ?? error.message);
            setIsLoading(false);
            displayError(error.response?.data ?? error.message, 'Failed to load delivery challan list');
        });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        setListViewConfig(config);
        loadDeliveryChallanList(config);
    }, [loadDeliveryChallanList]);

    useEffect(() => {
        getListViewSettings(cookies, 'delivery_challan')
            .then((response) => {
                const config = response.config || {};
                setListViewConfig(config);
                loadDeliveryChallanList(config);
            })
            .catch((error) => {
                displayError(error.response?.data ?? error.message, 'Failed to load list settings');
            });
    }, [cookies, loadDeliveryChallanList, refreshKey]);

    const columns = useMemo(
        () => [
            { fieldName: "delivery_challan_no", headerName: 'DC.No', size: 72 },
            { fieldName: "document_type", headerName: 'Document Type', size: 140 },
            { fieldName: "delivery_challan_date", headerName: 'DC Date', size: 86 },
            { fieldName: "plant", headerName: 'Plant', size: 65 },
            { fieldName: "delivery_challan_time", headerName: 'DC Time', size: 72 },
            { fieldName: "order_no", headerName: 'SO No', size: 88 },
            { fieldName: "workschedule_no", headerName: 'WS No', size: 72 },
            { fieldName: 'customer_name', headerName: 'Customer', size: 120 },
            { fieldName: 'sales_rep_name', headerName: 'Marketing', size: 95 },
            { fieldName: "consignee_projectname", headerName: 'Site/Project', size: 120 },
            { fieldName: 'production_plant', headerName: 'Prod. Plant', size: 95 },
            { fieldName: 'grade', headerName: 'Grade', size: 75 },
            { fieldName: "production_qty", headerName: 'Prod Qty', size: 72, align: 'right' },
            { fieldName: "purchase_qty", headerName: 'Pur Qty', size: 72, align: 'right' },
            { fieldName: "received_qty", headerName: 'Rec Qty', size: 72, align: 'right' },
            { fieldName: "other_qty", headerName: 'Oth Qty', size: 72, align: 'right' },
            { fieldName: "client_adjust_qty", headerName: 'Client Qty', size: 78, align: 'right' },
            { fieldName: "dc_qty", headerName: 'DC Qty', size: 68, align: 'right' },
            { fieldName: "accepted_qty", headerName: 'Acc Qty', size: 72, align: 'right' },
            { fieldName: "invoice_qty", headerName: 'Inv Qty', size: 72, align: 'right' },
            { fieldName: "customer_vehicle_no", headerName: 'Vehicle', size: 88 },
            { fieldName: "delivery_mode", headerName: 'Delivery', size: 82 },
            { fieldName: "status", headerName: 'Status', size: 78 },
            { fieldName: "dc_invioce", headerName: 'Inv Status', size: 88 },
            { fieldName: "id", headerName: "ROW_ACTIONS" },
        ],
        [],
    );

    const tableColumns = useMemo(
        () => (viewOnlyProp
            ? columns.filter((col) => col.headerName !== 'ROW_ACTIONS')
            : columns),
        [columns, viewOnlyProp],
    );

const onEditDeliveryChallan = (deliveryId, event) => {
    navigate(`/DeliveryChallanEdit/${deliveryId}/`);
};

const onDeleteDeliveryChallan = (deliveryId, event) => {
    event.preventDefault();
    Swal.fire({title: 'Are you sure to Delete?',  
    showCancelButton: true,  
    confirmButtonText: `Yes`,  
    cancelButtonText: `No`,
    }).then((result) => {  
        if (result.isConfirmed) { 

        setIsLoading(true);
        deleteDeliveryChallan(cookies, deliveryId)
        .then(response => {
            console.log(`Delivery with id ${deliveryId} deleted`);
            Swal.fire('Deleted Successfully!', '', 'success');
            setRefreshKey(oldKey => oldKey +1)
        })
        .catch(error => {
            setRefreshKey(oldKey => oldKey +1);
            console.log(error.response.data);
            displayError(error.response.data,'Delete Failed');
        });  
        } else if (result.isDismissed) {    
        Swal.fire('Not Deleted', '', 'info')  
    }
    });
	  
};

const onPrintBr = (deliveryId, event) => {
    event.preventDefault();
    setIsLoading(true);
    printBatchReport(cookies, deliveryId)
    .then(response => {
        openPdfResponse(response, {
            title: 'Batch Report',
            downloadFileName: 'batch_report.pdf',
        });
    })
    .catch(async (error) => {
        let payload = error?.response?.data ?? error;
        if (typeof Blob !== 'undefined' && payload instanceof Blob) {
            try {
                payload = JSON.parse(await payload.text());
            } catch (e) {
                payload = { message: error?.message || 'Batch report print failed.' };
            }
        }
        displayError(payload, 'BR print Failed');
    })
    .finally(() => setIsLoading(false));  
};

const onPrintDeliveryChallan = (deliveryId, event) => {
    event.preventDefault();
    setIsLoading(true);
    printDeliveryChallan(cookies, deliveryId)
    .then(response => {
        openPdfResponse(response, {
            title: 'Delivery Challan',
            downloadFileName: 'delivery_challan.pdf',
        });
        setIsLoading(false);
    })
    .catch(error => {
        console.log(error);
        displayError(error,'Print Failed');
        setIsLoading(false);
    });  
};

const handleExcelExport = async () => {
    const documentTitle = 'List of Delivery Challans';
    const exportHeader = await buildListExcelExportHeader(cookies, listViewConfig?.plant_ids, {
        documentTitle,
    });
    const infoLines = [...exportHeader.infoLines];
    if (!exportHeader.plantAlias && data.records[0]?.plant) {
        infoLines.unshift(`Plant: ${data.records[0].plant}`);
    }
    await exportAsCSV(
        tableColumns,
        data.records.map((dc) => ({
            plant: dc.plant,
            delivery_challan_no: dc.delivery_challan_no,
            document_type: dc.document_type,
            delivery_challan_date: dc.delivery_challan_date,
            delivery_challan_time: dc.delivery_challan_time,
            batch_no: dc.batch_no,
            order_no: dc.order_no,
            workschedule_no: dc.workschedule_no,
            customer_name: dc.customer_name,
            sales_rep_name: dc.sales_rep_name,
            consignee_projectname: dc.consignee_projectname,
            production_plant: dc.production_plant,
            grade: dc.grade,
            production_qty: dc.production_qty,
            purchase_qty: dc.purchase_qty,
            other_qty: dc.other_qty,
            received_qty: dc.received_qty,
            client_adjust_qty: dc.client_adjust_qty,
            dc_qty: dc.dc_qty,
            accepted_qty: dc.accepted_qty,
            invoice_qty: dc.invoice_qty,
            customer_vehicle_no: dc.customer_vehicle_no,
            delivery_mode: dc.delivery_mode,
            status: dc.status,
            dc_invioce: dc.dc_invioce,
        })),
        viewOnlyProp ? 'qc_delivery_challan_list' : 'delivery_challan_list',
        {
            documentTitle,
            infoLines,
            header: {
                companyName: exportHeader.companyName,
                addressLines: exportHeader.addressLines,
                gstNo: exportHeader.gstNo,
                logoDataUrl: exportHeader.logoDataUrl,
            },
            totalKeys: [
                'production_qty',
                'purchase_qty',
                'other_qty',
                'received_qty',
                'client_adjust_qty',
                'dc_qty',
                'accepted_qty',
                'invoice_qty',
            ],
        },
    );
};

const handlePDFExport = (event) => {
    console.log('PDF Export');
    exportAsPDF(
        'List of Delivery Challan',
        columns
        .filter(col => col.headerName !== 'ROW_ACTIONS')
        .map(col=>({header: col.headerName, dataKey: col.fieldName })),
        data.records.map(dc => ({
            plant:dc.plant,
            delivery_challan_no: dc.delivery_challan_no,
            document_type:dc.document_type,
            delivery_challan_date:dc.delivery_challan_date,
            delivery_challan_time:dc.delivery_challan_time,
            batch_no:dc.batch_no,
            order_no:dc.order_no,
            workschedule_no:dc.workschedule_no,
            customer_name:dc.customer_name,
            consignee_projectname:dc.consignee_projectname,
            production_plant:dc.production_plant,
            production_qty:dc.production_qty,
            purchase_qty:dc.purchase_qty,
            other_qty:dc.other_qty,
            received_qty:dc.received_qty,
            client_adjust_qty:dc.client_adjust_qty,
            dc_qty:dc.dc_qty,
            accepted_qty:dc.accepted_qty,
            invoice_qty:dc.invoice_qty,
            customer_vehicle_no:dc.customer_vehicle_no,
            delivery_mode:dc.delivery_mode,
            grade:dc.grade,
            dc_invioce:dc.dc_invioce,
            status:dc.status? "Active":"Cancelled",
            id: dc.id,    
        })),
        'DeliveryChallanList.pdf'
    );
};

return (

    <div id="outer-container"  className="App" > 
        <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
        <div id="page-wrap">
            <ListPageFloatingControls
                formLink={viewOnlyProp ? undefined : "/DeliveryChallan"}
                addNewLabel="New Delivery Challan"
                homeLink="/Home"
            />
        <div className="container">
            <PremiumPageHeader
                module={viewOnlyProp ? "QUALITY CONTROL" : "DISPATCH"}
                title="List of Delivery Challan"
                subtitle={viewOnlyProp
                    ? "View delivery challans for quality control. This list is read-only."
                    : "Configure filters in General Settings. The table loads based on your saved preferences."}
                modeLabel="VIEW MODE"
                headerActions={(
                    <ListViewSettings
                        viewKey="delivery_challan"
                        onSaved={handleSettingsSaved}
                    />
                )}
            />
        </div>

            <Box
                className="container"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: { xs: 'wrap', lg: 'nowrap' },
                    mb: 2.5,
                    py: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        flexShrink: 0,
                        flexWrap: 'wrap',
                    }}
                >
                    <Box
                        component="button"
                        type="button"
                        className="today-dc-card-button"
                        onClick={() => setTodayDcModalOpen(true)}
                        title="View today's delivery challan details"
                        sx={{
                            flex: '0 0 auto',
                            minWidth: 128,
                            borderRadius: '12px',
                            background: todayDcCard.gradient,
                            boxShadow: todayDcCard.shadow,
                            px: 1.75,
                            py: 1.1,
                            color: '#fff',
                            whiteSpace: 'nowrap',
                            textAlign: 'right',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '0.68rem',
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                opacity: 0.9,
                                lineHeight: 1.2,
                            }}
                        >
                            {todayDcCard.label}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '1.35rem',
                                fontWeight: 700,
                                lineHeight: 1.15,
                                letterSpacing: '-0.02em',
                                mt: 0.25,
                            }}
                        >
                            {todayDcCard.value}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        gap: 1.25,
                        flex: 1,
                        justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                        minWidth: 0,
                        overflowX: 'auto',
                    }}
                >
                    {summaryCards.map((card) => (
                        <Box
                            key={card.label}
                            sx={{
                                flex: '0 0 auto',
                                minWidth: 128,
                                borderRadius: '12px',
                                background: card.gradient,
                                boxShadow: card.shadow,
                                px: 1.75,
                                py: 1.1,
                                color: '#fff',
                                position: 'relative',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textAlign: 'right',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '0.68rem',
                                    fontWeight: 600,
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase',
                                    opacity: 0.9,
                                    lineHeight: 1.2,
                                }}
                            >
                                {card.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '1.35rem',
                                    fontWeight: 700,
                                    lineHeight: 1.15,
                                    letterSpacing: '-0.02em',
                                    mt: 0.25,
                                }}
                            >
                                {card.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        
            <div className="container item-list-table-container">
                <div className="table-responsive">
                <ReactTableWrapper
                            title='List of DeliveryChallan'
                            columns={tableColumns}
                            data={data.records}
                            onRowEdit={viewOnlyProp ? undefined : onEditDeliveryChallan}
                            onRowDelete={viewOnlyProp ? undefined : onDeleteDeliveryChallan}
                            onCSVExport={handleExcelExport}
                            onPDFExport={viewOnlyProp ? undefined : handlePDFExport}
                            isLoading={isLoading}
                            hideDelete={true}
                            onRowBrPrint={viewOnlyProp ? undefined : onPrintBr}
                            onRowPrint={viewOnlyProp ? undefined : onPrintDeliveryChallan}
                            hidePrint={viewOnlyProp}
                            left_pinned={viewOnlyProp ? undefined : left_pinned}
                            right_pinned={viewOnlyProp ? undefined : right_pinned}
                            compactRows={true}
                        />
                </div>
            </div>
            <TodayDcDetailModal
                open={todayDcModalOpen}
                onClose={() => setTodayDcModalOpen(false)}
                cookies={cookies}
                listViewConfig={listViewConfig}
            />
        </div>
    </div>
);

}



export default DeliveryChallanTable;
